<template>
  <BaseDialogForm :is-edit="isEdit">
    <template v-slot:toggle="{ on }">
      <v-btn v-if="isDuplicate" icon v-on="on" @click.stop="onOpen()" small>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" small>
              file_copy
            </v-icon>
          </template>
          <span>Duplicate</span>
        </v-tooltip>
      </v-btn>
      <v-btn
        v-else-if="!isEdit"
        color="primary"
        v-on="on"
        @click.stop="onOpen()"
      >
        <v-icon left>
          add_box
        </v-icon>
        New Work Order
      </v-btn>
      <v-btn v-else icon v-on="on" @click.stop="onOpen()" small>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" small>
              edit
            </v-icon>
          </template>
          <span>Edit</span>
        </v-tooltip>
      </v-btn>
    </template>
    <template v-slot:title>
      <h2 v-if="isEdit">Edit {{ instance.name }}</h2>
      <h2 v-else>Make A New WorkOrder</h2>
    </template>
    <template v-slot:content>
      <v-form v-model="$v.valid" @submit.prevent="submit">
        <v-text-field
          v-model.trim="$v.formdata.name.$model"
          :error-messages="nameErrors"
          label="WorkOrder Name or Title"
          counter="124"
          hint="Something that briefly describes this Work Order"
          prepend-icon="fas fa-id-card"
          @input="$v.formdata.name.$touch()"
          @blur="$v.formdata.name.$touch()"
        />
        <v-autocomplete
          v-model="$v.formdata.material_id.$model"
          :error-messages="materialErrors"
          label="Material Selection"
          :items="materials"
          :loading="materialLoading"
          item-value="id"
          item-text="display_name"
          prepend-icon="fas fa-atlas"
          @input="$v.formdata.material_id.$touch()"
          @blur="$v.formdata.material_id.$touch()"
        />
        <v-autocomplete
          v-model="$v.formdata.coating_id.$model"
          :error-messages="coatingErrors"
          label="Coating Selection"
          :items="coatings"
          :loading="coatingLoading"
          item-value="id"
          item-text="name"
          hint="Select the chemistry applied the substrate"
          prepend-icon="add_box"
          append-icon="fas fa-atom"
          @click:prepend="showCoatingForm = !showCoatingForm"
          @input="$v.formdata.coating_id.$touch()"
          @blur="$v.formdata.coating_id.$touch()"
        />
        <v-row justify="center">
          <v-col sm="8" cols="8">
            <CoatingFormNew v-show="showCoatingForm" />
          </v-col>
        </v-row>
        <v-text-field
          v-model="$v.formdata.coatedquantity.$model"
          :error-messages="coatedquantityErrors"
          type="number"
          label="Coated Quantity"
          counter="13"
          hint="Total quantity coated"
          prepend-icon="fas fa-weight"
          :suffix="formdata.units"
          @input="$v.formdata.coatedquantity.$touch()"
          @blur="$v.formdata.coatedquantity.$touch()"
        />
        <v-text-field
          v-model="$v.formdata.witnessquantity.$model"
          :error-messages="witnessquantityErrors"
          type="number"
          label="Witness Quantity"
          counter="13"
          hint="Total quanity uncoated"
          prepend-icon="fas fa-weight"
          :suffix="formdata.units"
          @input="$v.formdata.witnessquantity.$touch()"
          @blur="$v.formdata.witnessquantity.$touch()"
        />
        <v-text-field
          v-model="$v.formdata.batchquantity.$model"
          :error-messages="batchquantityErrors"
          type="number"
          label="Batch Size"
          counter="13"
          hint="Actual running batch size"
          prepend-icon="fas fa-weight"
          :suffix="formdata.units"
          @input="$v.formdata.batchquantity.$touch()"
          @blur="$v.formdata.batchquantity.$touch()"
        />
        <v-select
          v-model="$v.formdata.units.$model"
          :error-messages="unitErrors"
          prepend-icon="mdi-scale-balance"
          label="Units"
          :items="units"
          @input="$v.formdata.units.$touch()"
          @blur="$v.formdata.units.$touch()"
        />
        <v-switch
          v-model="formdata.picoshield"
          label="Picoshield Batch"
          hint="Check this if you know this is desginated for picoshield"
        />
      </v-form>
    </template>

    <template v-slot:actions="{ close }">
      <v-btn color="warning" dark @click="clear">
        <v-icon left>fas fa-redo</v-icon>
        Clear
      </v-btn>
      <v-btn
        :disabled="$v.$invalid"
        color="primary"
        @click="
          submit();
          close();
        "
      >
        <v-icon dark left>
          fas fa-save
        </v-icon>
        Save
      </v-btn>
    </template>
  </BaseDialogForm>
</template>

<script>
import WorkOrder from "../../models/WorkOrder";
import Coating from "../../models/Coating";
import Material from "../../models/Material";
import BaseDialogForm from "../Base/BaseDialogForm";
import CoatingFormNew from "./CoatingFormNew";
import { validationMixin } from "vuelidate";
import { handleErrors } from "../../services/formErrors";
export default {
  name: "WorkOrderForm",
  components: { BaseDialogForm, CoatingFormNew },
  mixins: [validationMixin],
  validations: WorkOrder.validations,
  props: {
    instance: {
      type: Object,
      default: () => {}
    },
    isDuplicate: {
      type: Boolean,
      required: true,
      default: false
    },
    isEdit: {
      type: Boolean,
      required: true,
      default: false
    },
    projid: {
      type: [Number, String],
      required: true,
      default: 1
    }
  },
  data() {
    return {
      coatingLoading: true,
      materialLoading: true,
      showCoatingForm: false,
      units: ["g", "kg", "mg", 'mL', 'L', "units"],
      formdata: new WorkOrder(this.instance)
    };
  },
  computed: {
    // return error messages for validated fields
    nameErrors() {
      return handleErrors("formdata", "name", this);
    },
    coatedquantityErrors() {
      return handleErrors("formdata", "coatedquantity", this);
    },
    witnessquantityErrors() {
      return handleErrors("formdata", "witnessquantity", this);
    },
    batchquantityErrors() {
      return handleErrors("formdata", "batchquantity", this);
    },
    coatingErrors() {
      return handleErrors("formdata", "coating_id", this);
    },
    materialErrors() {
      return handleErrors("formdata", "material_id", this);
    },
    unitErrors() {
      return handleErrors("formdata", "units", this);
    },
    materials() {
      return Material.query().orderBy('id').get();
    },
    // For coating selection field
    coatings() {
      return Coating.all();
    }
  },
  methods: {
    onOpen() {
      Material
        .$fetchActive()
        .then(() => (this.materialLoading = false));
      Coating.$fetch().then(() => (this.coatingLoading = false));
    },
    submit() {
      this.$v.$touch();
      if (this.isEdit) {
        WorkOrder.$put(this.instance.id, this.formdata.toUpdatePayload())
      } else {
        this.formdata.project_id = this.projid;
        WorkOrder
          .$post(this.formdata.toCreatePayload())
          .then(() => this.clear())
      }
    },
    clear() {
      this.formdata = new WorkOrder();
      this.$v.$reset();
    }
  }
};
</script>
